/**
 * Where the Campuzon configuration service lives.
 *
 * Set `VITE_CAMPUZON_API_URL` (build environment) or, for a running
 * deployment, `window.__CAMPUZON_API_URL__`. When neither is present there is
 * no configuration server to talk to, and the platform says so plainly rather
 * than presenting a browser-local record as production state.
 */
declare global {
  interface Window {
    __CAMPUZON_API_URL__?: string
  }
}

function fromEnv(): string | null {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    const value = env?.VITE_CAMPUZON_API_URL
    return value && value.trim().length > 0 ? value.trim() : null
  } catch {
    return null
  }
}

function fromWindow(): string | null {
  if (typeof window === 'undefined') return null
  const value = window.__CAMPUZON_API_URL__
  return value && value.trim().length > 0 ? value.trim() : null
}

/** The API origin, without a trailing slash, or null when none is configured. */
export const API_BASE_URL: string | null = (() => {
  const value = fromWindow() ?? fromEnv()
  return value ? value.replace(/\/+$/, '') : null
})()

export const HAS_API = API_BASE_URL !== null

export function apiUrl(path: string): string {
  if (!API_BASE_URL) throw new Error('No Campuzon API is configured.')
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Configuration must never be served from a stale cache — a published change
 * that a CDN holds onto is indistinguishable from a change that did not
 * happen. Every request opts out explicitly.
 */
export const NO_CACHE: RequestInit = {
  cache: 'no-store',
  headers: { 'Cache-Control': 'no-cache', Accept: 'application/json' },
  credentials: 'include',
}

/** A request that fails fast rather than leaving a surface waiting. */
export async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 8000)
  try {
    const response = await fetch(apiUrl(path), {
      ...NO_CACHE,
      ...init,
      headers: { ...NO_CACHE.headers, ...(init.headers ?? {}) },
      signal: controller.signal,
    })
    if (!response.ok) {
      throw new Error(`The configuration service answered ${response.status}.`)
    }
    return (await response.json()) as T
  } finally {
    window.clearTimeout(timeout)
  }
}
